import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { ResourceActiveFilterChip } from '@/shared/ui/Layout'

import type { StorePartnerFilter } from '../components/StoreAdvancedFilter'
import { deriveStoreCategoryOptions } from '../lib/deriveStoreCategoryOptions'
import { deriveStorePartnerOptions } from '../lib/deriveStorePartnerOptions'
import {
  filterStoreCatalogItems,
  isStoreItemSoldOut
} from '../lib/filterStoreCatalogItems'
import {
  EMPTY_STORE_ADVANCED_FILTERS,
  type StoreAdvancedFilters,
  type StoreCatalogItem
} from '../lib/types'

const SEARCH_DEBOUNCE_MS = 400

type PartnerCategoryLookup = Record<string, { id: string; name: string }>

type UseStoreListFiltersArgs = {
  rewardItems: StoreCatalogItem[]
  isAvailable: (item: StoreCatalogItem) => boolean
  walletCoins: number | undefined
  walletReady: boolean
  partnerNameById?: Record<string, string>
}

type CatalogPillKey = 'available' | 'soldOut' | 'requested' | 'affordable'

// "Apenas disponíveis" e "Esgotados" são opostos diretos: ligados juntos o
// segundo não produziria efeito nenhum, e a tela não conseguiria explicar isso.
const OPPOSITE_CATALOG_PILL: Partial<Record<CatalogPillKey, CatalogPillKey>> = {
  available: 'soldOut',
  soldOut: 'available'
}

export const useStoreListFilters = ({
  rewardItems,
  isAvailable,
  walletCoins,
  walletReady,
  partnerNameById = {}
}: UseStoreListFiltersArgs) => {
  const [searchInput, setSearchInput] = useState('')
  const [committedSearch, setCommittedSearch] = useState('')
  const [advancedFilters, setAdvancedFilters] = useState<StoreAdvancedFilters>(
    EMPTY_STORE_ADVANCED_FILTERS
  )
  const searchDebounceRef = useRef<number | null>(null)

  // Só o predicado de esgotado, nunca `filteredRewardItems`: as opções saídas
  // da lista já filtrada colapsariam na própria seleção, sem caminho de volta
  // para as demais. Lê o estado cru porque `effectiveAdvanced` depende destas
  // opções para neutralizar a seleção órfã.
  const stockVisibleRewardItems = useMemo(
    () =>
      advancedFilters.soldOut
        ? rewardItems
        : rewardItems.filter(item => !isStoreItemSoldOut(item)),
    [rewardItems, advancedFilters.soldOut]
  )

  // O único derivado que precisa do catálogo bruto: a pergunta é sobre o evento,
  // não sobre a listagem. Tirá-lo de `stockVisibleRewardItems` responderia
  // sempre "não há" — é dali que os esgotados acabaram de ser removidos — e
  // tirá-lo de `filteredRewardItems` confundiria "a grade está vazia" com "não
  // há esgotado no evento". `some` para em cima do primeiro, então só percorre
  // o catálogo inteiro no caso em que a resposta é falsa.
  const hasSoldOutItems = useMemo(
    () => rewardItems.some(isStoreItemSoldOut),
    [rewardItems]
  )

  const partnerOptions = useMemo(
    () => deriveStorePartnerOptions(stockVisibleRewardItems, partnerNameById),
    [stockVisibleRewardItems, partnerNameById]
  )

  const categoryOptions = useMemo(
    () => deriveStoreCategoryOptions(stockVisibleRewardItems),
    [stockVisibleRewardItems]
  )

  const showPartnerAdvancedFilter =
    partnerOptions.length > 0 || categoryOptions.length > 0

  useEffect(() => {
    searchDebounceRef.current = window.setTimeout(() => {
      setCommittedSearch(prev => (prev === searchInput ? prev : searchInput))
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current)
      }
    }
  }, [searchInput])

  const flushSearch = useCallback(() => {
    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current)
      searchDebounceRef.current = null
    }

    setCommittedSearch(searchInput)
  }, [searchInput])

  const toggleCatalogPill = useCallback(
    (key: CatalogPillKey) => {
      setAdvancedFilters(prev => {
        if (key === 'affordable' && !walletReady) {
          return prev
        }

        const enabled = !prev[key]
        const opposite = OPPOSITE_CATALOG_PILL[key]
        const next = { ...prev, [key]: enabled }

        if (enabled && opposite) {
          next[opposite] = false
        }

        return next
      })
    },
    [walletReady]
  )

  const applyPartnerDraft = useCallback((draft: StorePartnerFilter) => {
    setAdvancedFilters(prev => ({
      ...prev,
      partnerId: draft.partnerId,
      categoryId: draft.categoryId
    }))
  }, [])

  const resetPartnerAdvanced = useCallback(() => {
    setAdvancedFilters(prev => ({
      ...prev,
      partnerId: null,
      categoryId: null
    }))
  }, [])

  const removeChip = useCallback((id: string) => {
    if (id === 'partner') {
      setAdvancedFilters(prev => ({ ...prev, partnerId: null }))
      return
    }

    if (id === 'category') {
      setAdvancedFilters(prev => ({ ...prev, categoryId: null }))
    }
  }, [])

  // Seleção que saiu das opções é neutralizada por derivação, não limpa por
  // evento: o estoque pode voltar pelo poll ou por uma reserva cancelada, e aí
  // a escolha do Participante volta a valer sozinha. `soldOut` fica de fora —
  // é ele que comanda as opções, e neutralizá-lo apagaria o próprio pill.
  const effectiveAdvanced = useMemo(
    () => ({
      ...advancedFilters,
      affordable: walletReady ? advancedFilters.affordable : false,
      partnerId: partnerOptions.some(
        option => option.id === advancedFilters.partnerId
      )
        ? advancedFilters.partnerId
        : null,
      categoryId: categoryOptions.some(
        option => option.id === advancedFilters.categoryId
      )
        ? advancedFilters.categoryId
        : null
    }),
    [advancedFilters, walletReady, partnerOptions, categoryOptions]
  )

  const filteredRewardItems = useMemo(
    () =>
      filterStoreCatalogItems(rewardItems, {
        search: committedSearch,
        advanced: effectiveAdvanced,
        walletCoins: walletCoins ?? 0,
        walletReady,
        isAvailable,
        partnerNameById
      }),
    [
      rewardItems,
      committedSearch,
      effectiveAdvanced,
      walletCoins,
      walletReady,
      isAvailable,
      partnerNameById
    ]
  )

  const partnerFilters: StorePartnerFilter = useMemo(
    () => ({
      partnerId: effectiveAdvanced.partnerId,
      categoryId: effectiveAdvanced.categoryId
    }),
    [effectiveAdvanced.partnerId, effectiveAdvanced.categoryId]
  )

  const activeChips = useMemo((): ResourceActiveFilterChip[] => {
    const chips: ResourceActiveFilterChip[] = []

    if (effectiveAdvanced.partnerId) {
      const partnerLabel = partnerOptions.find(
        option => option.id === effectiveAdvanced.partnerId
      )?.label

      if (partnerLabel) {
        chips.push({ id: 'partner', label: `Parceiro: ${partnerLabel}` })
      }
    }

    if (effectiveAdvanced.categoryId) {
      const categoryLabel = categoryOptions.find(
        option => option.id === effectiveAdvanced.categoryId
      )?.label

      if (categoryLabel) {
        chips.push({ id: 'category', label: `Categoria: ${categoryLabel}` })
      }
    }

    return chips
  }, [
    effectiveAdvanced.partnerId,
    effectiveAdvanced.categoryId,
    partnerOptions,
    categoryOptions
  ])

  const hasActivePartnerAdvanced = Boolean(
    effectiveAdvanced.partnerId || effectiveAdvanced.categoryId
  )

  return {
    searchInput,
    setSearchInput,
    flushSearch,
    advancedFilters: effectiveAdvanced,
    partnerFilters,
    showPartnerAdvancedFilter,
    hasActivePartnerAdvanced,
    applyPartnerDraft,
    resetPartnerAdvanced,
    partnerOptions,
    categoryOptions,
    hasSoldOutItems,
    activeChips,
    removeChip,
    filteredRewardItems,
    toggleAvailable: () => toggleCatalogPill('available'),
    toggleSoldOut: () => toggleCatalogPill('soldOut'),
    toggleRequested: () => toggleCatalogPill('requested'),
    toggleAffordable: () => toggleCatalogPill('affordable')
  }
}

export type { StoreAdvancedFilters }
